/**
 * main-readout-content.ts — authored narrative for the /main "Summary" readout tab.
 *
 * WHAT THIS IS
 * ------------
 * The Summary tab pairs a HUMAN-authored exec narrative with LIVE Salesforce
 * numbers. This file is the human half: the framing, the arc, the judgement
 * calls an exec reads to understand "what the numbers mean". The numbers
 * themselves are never hard-coded here — they are injected at render time from
 * the live /api/main/scorecard payload via {{token}} placeholders (see
 * main-readout.ts for the token grammar), so the prose can be reviewed on a
 * cadence while the figures stay current to the last cache refresh.
 *
 * HOW TO UPDATE (each reporting cycle)
 * ------------------------------------
 *   1. Re-read the sections below and adjust the framing to match reality
 *      (e.g. if a channel changed, if a target moved, if the story shifted).
 *   2. Bump READOUT_REVIEWED_ON to the date you reviewed it.
 *   3. Leave the {{tokens}} alone unless a metric id changed — they self-update.
 *
 * TOKEN GRAMMAR (resolved by main-readout.ts):
 * Each kind mirrors the EXACT formatter its tile uses (see main-readout.ts) so a
 * number reads identically here and on the tab:
 *   {{MAIN-24}}              → single-metric value, formatted by its unit
 *   {{MAIN-24|target}}       → that metric's target value
 *   {{MAIN-24|pctTarget}}    → single-metric value as % of target (tile's chip)
 *   {{MAIN-04|count}}        → count activity headline (formatCountInt)
 *   {{MAIN-32|total}}        → generic bucketed headline (≥$1M as $, else int)
 *   {{MAIN-12|qtrUsd}}       → unit-quarter currency headline ("$82k")
 *   {{MAIN-20|days}}         → unit-quarter days headline ("243.0 days")
 *   {{MAIN-04|wow}}          → WoW change as the tile's pill shows it: an
 *                              ABSOLUTE count ("up 120" / "down 80" / "flat")
 *   {{MAIN-04|mom}}          → MoM change, same absolute-count form
 *   {{MAIN-21|trend}}        → snapshot direction, e.g. "down 8% on the week"
 *   MAIN-17 FY pacing (derived from the MAIN-48 series the way MainPacingTile
 *   does — coverage target is NET of Closed-Won, matching the tile headline):
 *   {{MAIN-17|fyActual}}     → OPEN pipeline for the FY to date (cumulative by close month)
 *   {{MAIN-17|fyTarget}}     → coverage target the tile leads with (net of CW)
 *   {{MAIN-17|fyGrossTarget}}→ gross 3× FY target before CW is backed out
 *   {{MAIN-17|fyCoverage}}   → coverage as % of the coverage target
 *   {{MAIN-17|fyClosedWon}}  → Closed-Won FY already booked
 *   {{MAIN-17|fyPaceWord}}   → "above the coverage target" / "below the coverage target"
 * quick-261104-ia3 adds two prefixed families (main-readout.ts):
 *   {{RILLET|arr}} {{RILLET|arrYoY}} {{RILLET|churn12}} {{RILLET|churn12Pct}}
 *   {{RILLET|lastChurn}} {{RILLET|lastChurnMonth}} {{RILLET|nrr}} {{RILLET|grr}}
 *   {{RILLET|renewalsOpen90}}  (dated Rillet constant)
 *   {{EXEC|pipelineOpen}} {{EXEC|potentialCount}} {{EXEC|potentialValue}}
 *   {{EXEC|nextYearPipe}} {{EXEC|nextYearPct}} {{EXEC|nlWin}} {{EXEC|expWin}}
 *                            (GET /api/main/exec)
 * 2026-11-04 rewrite adds atomic values and direction-safe PHRASE tokens (each
 * reads correctly whether the data moved up or down, and falls back to a
 * neutral phrase when data is missing) — see main-readout.ts for the full list:
 *   RILLET|customers arrYoYAmt arrMonthsUp expansion12 largestChurn
 *     largestChurnShare churnShape provisionalNote nrrRead top5Share top1Share
 *     concentrationRead
 *   EXEC|pipelineWithPotential potentialPctOfOpen pipeChange12 topStage
 *     meetingsTrend nextYearTarget nlWinVsLastYear dealSizeMix cycleGap
 *     mktTopChannel mktPipeTrend mktVolumeVsQuality
 * Any token whose data is missing/null renders an em dash "—" or a neutral
 * phrase — the readout never fabricates a number, and never hard-codes one.
 */

/** Date the narrative framing below was last reviewed by a human. */
export const READOUT_REVIEWED_ON = '2026-11-04';

/** Short "how to read this" preamble shown at the top of the Summary tab. */
export const READOUT_INTRO =
  'The one-screen readout of the sales dashboard. Figures come live from Salesforce on every ' +
  'refresh; recurring revenue and churn come from Rillet. It answers five questions in order: ' +
  'is recurring revenue growing, are we keeping customers, are we generating real pipeline, are ' +
  'we closing better deals, and is marketing producing pipeline. Each section links to the tab ' +
  'with the detail. Hover the (i) on any tile above for a plain-English explanation.';

export interface ReadoutHighlight {
  /** Short chip label, e.g. "Outbound (30d)". */
  label: string;
  /** Token spec resolved against the live data, e.g. "MAIN-04|count" or "RILLET|arr". */
  token: string;
}

export interface ReadoutSection {
  id: string;
  /** The exec question this section answers — rendered as the heading. */
  question: string;
  /** One-line framing under the heading. */
  standfirst: string;
  /** Which /main tab this maps to, for the "see the detail" affordance. */
  tab: 'sales' | 'marketing' | 'cs' | 'finance';
  tabLabel: string;
  /** Authored paragraphs; may embed {{token}} placeholders. */
  body: string[];
  /** One durable, token-free strategic sentence on what the data means. */
  strategic: string;
  /** Compact key-number strip rendered under the prose. */
  highlights: ReadoutHighlight[];
}

export const READOUT_SECTIONS: ReadoutSection[] = [
  {
    id: 'revenue',
    question: 'Is recurring revenue growing?',
    standfirst: 'ARR, what we added and what we lost.',
    tab: 'finance',
    tabLabel: 'Finance',
    body: [
      'ARR stands at {{RILLET|arr}} across {{RILLET|customers}} paying customers, {{RILLET|arrYoY}} on a ' +
        'year ago ({{RILLET|arrYoYAmt}}). Month to month, ARR {{RILLET|arrMonthsUp}}, which says more ' +
        'about the underlying trend than any single month does.',
      'Over the same 12 months we lost {{RILLET|churn12}} to customers leaving or renewing for less, ' +
        '{{RILLET|churn12Pct}} of the ARR we started the year with. The a-ha is in how those losses ' +
        'were shaped: they were {{RILLET|churnShape}}.',
      'Because every loss sits behind a renewal date we already know, most of that exposure is ' +
        'visible months before it lands. The latest month, {{RILLET|lastChurnMonth}}, shows ' +
        '{{RILLET|lastChurn}} dropping off{{RILLET|provisionalNote}}.',
    ],
    strategic:
      'Recurring revenue compounds only when what we keep outruns what we lose, so retention is ' +
      'worth as much attention as new logos.',
    highlights: [
      { label: 'ARR', token: 'RILLET|arr' },
      { label: 'vs a year ago', token: 'RILLET|arrYoY' },
      { label: 'Lost, 12 months', token: 'RILLET|churn12' },
      { label: 'Largest single loss', token: 'RILLET|largestChurn' },
      { label: 'Latest month lost', token: 'RILLET|lastChurn' },
    ],
  },
  {
    id: 'keeping',
    question: 'Are we keeping customers?',
    standfirst: 'Retention, concentration and the renewals coming up.',
    tab: 'cs',
    tabLabel: 'Customer Success',
    body: [
      'Net revenue retention over the last 12 months is {{RILLET|nrr}} and gross retention is ' +
        '{{RILLET|grr}}. Put simply, {{RILLET|nrrRead}}: existing customers added ' +
        '{{RILLET|expansion12}} over the year against {{RILLET|churn12}} lost.',
      'The less obvious point is concentration: {{RILLET|concentrationRead}}. The single largest ' +
        'customer accounts for {{RILLET|top1Share}} of ARR on its own.',
      'Contracts ending in the next 90 days with no renewal booked yet: {{RILLET|renewalsOpen90}}. ' +
        'That list is the earliest warning the business gets, and it is cheapest to act on while the ' +
        'date is still weeks away.',
    ],
    strategic:
      'Renewals decided late are renewals at risk: the list of contracts ending without a booked ' +
      'renewal is the earliest warning the business has.',
    highlights: [
      { label: 'Net retention', token: 'RILLET|nrr' },
      { label: 'Gross retention', token: 'RILLET|grr' },
      { label: 'Top 5 share of ARR', token: 'RILLET|top5Share' },
      { label: 'Ending in 90d, not renewed', token: 'RILLET|renewalsOpen90' },
    ],
  },
  {
    id: 'generating',
    question: 'Are we generating real pipeline?',
    standfirst: 'The open funnel, potential opps and the meetings that feed them.',
    tab: 'sales',
    tabLabel: 'Sales',
    body: [
      'Open product pipeline is {{EXEC|pipelineOpen}}, or {{EXEC|pipelineWithPotential}} once the ' +
        '{{EXEC|potentialCount}} potential opps are counted at our average deal size. Counting both, ' +
        'the total is {{EXEC|pipeChange12}}.',
      'The largest stage by dollars is {{EXEC|topStage}}.',
      'The a-ha is the potential pool: it adds {{EXEC|potentialPctOfOpen}} on top of the open ' +
        'pipeline, and every company in it has already sat down with us, so it is the closest ' +
        'pipeline we have that is not yet on the books.',
      'Meetings booked are the early read on next quarter: {{EXEC|meetingsTrend}}. Next year already ' +
        'has {{EXEC|nextYearPipe}} of open pipeline, {{EXEC|nextYearPct}} of the {{EXEC|nextYearTarget}} ' +
        'working target.',
    ],
    strategic:
      'Pipeline is built in a chain, so the leak to fix first is usually the meeting that never ' +
      'becomes an opportunity: the potential-opps list is that leak, named company by company.',
    highlights: [
      { label: 'Open pipeline', token: 'EXEC|pipelineOpen' },
      { label: 'With potential', token: 'EXEC|pipelineWithPotential' },
      { label: 'Potential opps', token: 'EXEC|potentialCount' },
      { label: 'Next-year pipeline', token: 'EXEC|nextYearPipe' },
      { label: 'Outbound (30d)', token: 'MAIN-04|count' },
      { label: 'Meetings booked (30d)', token: 'MAIN-01|count' },
    ],
  },
  {
    id: 'closing',
    question: 'Are we closing better deals?',
    standfirst: 'Win rate, cycle time, deal size and deals going quiet.',
    tab: 'sales',
    tabLabel: 'Sales',
    body: [
      'This year we win {{EXEC|nlWin}} of new-logo deals that reach a decision and {{EXEC|expWin}} of ' +
        'expansion deals; against last year, the new-logo rate {{EXEC|nlWinVsLastYear}}.',
      'On deal size, {{EXEC|dealSizeMix}}. The average deal we win overall is {{MAIN-12|qtrUsd}}, and it ' +
        'takes {{MAIN-20|days}} from opportunity to close.',
      'The a-ha is how much company size sets the clock: {{EXEC|cycleGap}}. That gap is worth building ' +
        'into close dates and forecasts rather than discovering deal by deal.',
      '{{MAIN-21}} open deals have stalled, with no stage change and no next-step update in three weeks.',
    ],
    strategic:
      'A bigger deal only turns into revenue faster if win rate and cycle time hold, so deal velocity, ' +
      'not any one number, is the measure to manage.',
    highlights: [
      { label: 'New-logo win rate', token: 'EXEC|nlWin' },
      { label: 'Expansion win rate', token: 'EXEC|expWin' },
      { label: 'Avg won deal', token: 'MAIN-12|qtrUsd' },
      { label: 'Sales cycle', token: 'MAIN-20|days' },
      { label: 'Stalled deals', token: 'MAIN-21' },
    ],
  },
  {
    id: 'marketing',
    question: 'Is marketing producing pipeline?',
    standfirst: 'Marketing-sourced pipeline and which channels convert.',
    tab: 'marketing',
    tabLabel: 'Marketing',
    body: [
      'Marketing is sourcing {{MAIN-32|total}} of pipeline right now. Over the last 12 months the ' +
        'largest channel was {{EXEC|mktTopChannel}}.',
      'Pipeline created from marketing sources: {{EXEC|mktPipeTrend}}.',
      'One thing worth noticing: {{EXEC|mktVolumeVsQuality}}. Volume and conversion do not always ' +
        'come from the same place, and conversion is usually where the next dollar goes further.',
      'The Marketing tab shows each channel over time, alongside win rate and days to win.',
    ],
    strategic:
      'Efficient growth is a channel-mix decision: put spend where leads convert and close, and starve ' +
      'the channels that only manufacture volume.',
    highlights: [{ label: 'Mkt-sourced pipeline', token: 'MAIN-32|total' }],
  },
];
